import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  CANCEL_BEFORE_START_NOTICE,
  CONTACT_EMAIL,
  MADE_SHEET_NO_REFUND_NOTICE,
  PRODUCT_CATEGORY_STATEMENT_COPY as CATEGORY_COPY,
  PAYMENT_FAILED_IMAGE_NOTICE,
  PRODUCT_CATEGORY_STATEMENT_OPTIONS,
  UNIT_PRICE_ROW_LABEL,
  UPLOAD_CLOSES_NOTICE,
  paymentOrderName,
  formatKrw,
  paymentClientErrorCode,
  type ConfirmPaymentRequest,
  type DetailJobView,
  type PaymentCheckoutView,
  type PaymentWidgetStage,
  type PointBalanceView,
  type ProductCategoryStatement,
  type WebPointChoice,
} from "@makit/shared";

import { PageHead } from "../../app/PageHead";
import { Button } from "../../components/primitives";
import { LimitBanner, PayNow } from "../../components/banners";
import { EmptyState } from "../../components/feedback";
import { SettingGroup, SettingRow } from "../../components/settings";
import {
  ApiError,
  confirmPayment,
  fetchPaymentCheckout,
  processJob,
  reportPaymentClientError,
  stateProductCategory,
} from "../../lib/api";
import {
  cardRevertedLines,
  isWidgetSelfCancel,
  paymentMessage,
  pointsAdjustedLines,
  widgetFailureMessage,
  type PaymentMessage,
} from "../../lib/payment-messages";
import type { ShellJob } from "../../lib/shell-job";
import {
  PaymentWidgetError,
  mountPaymentWidget,
  readWidgetReturn,
  widgetReturnUrl,
  type PaymentWidgetHandle,
} from "../../lib/toss-widget";
import { useJobView } from "../../lib/use-job-view";
import { PointChoice } from "../../features/pay/PointChoice";
import {
  PointsConfirmError,
  confirmPaymentWithPoints,
  checkoutPointUse,
  fetchPointsForPay,
  localMonthDay,
  payJobWithPoints,
  planForPay,
  pointsOffered,
  pointsPayOutcome,
  pointUseFor,
  pointUseLine,
  readReturnPointAmount,
  withReturnPointAmount,
  type ConfirmWithPointsRequest,
} from "../../lib/points-pay";

/** `W-26` 결제. 앱 `A-26`·`A-27`·`A-28`·`A-28b` 가 여기 모인다.
 *
 * **폰은 상태마다 화면을 갈랐고 웹은 오른쪽에 상태 셋을 나란히 편다.** 한 화면에서
 * "무엇이 벌어질 수 있는지"가 같이 보이는 것이 넓은 화면의 이점이다.
 *
 * ## ⭐ 2026-09-25. **이 화면이 진짜 결제를 한다**
 *
 * 그전까지 이 화면은 통째로 예시(`SAMPLE_PAY`)였고 **결제위젯이 저장소 어느 화면에도 붙어
 * 있지 않았다.** 서버 계약은 서 있는데(`GET /api/payments/checkout/:jobId` ·
 * `POST /api/payments/confirm`) **부르는 화면이 0건**이었다.
 *
 * ⛔ **예시 갈래(`SamplePay`)를 걷었다** (2026-10-04 사용자 지시). 예시 작업이 없어졌다.
 * 진짜 작업은 `LivePay` 가 받는다. ⛔ 예시 숫자를 이 화면에 되살리지 마라. **그 실수의 증상은
 * 셀러가 틀린 금액을 보는 것**이다.
 *
 * ## ⛔ 화면이 금액을 만들지 않는다
 *
 * 금액도 주문번호도 서버가 준다(`PaymentCheckoutView`). 화면이 만들면 승인 단계의 대조가
 * **화면이 만든 수와 화면이 만든 수**를 비교하는 일이 된다.
 */
/* **결제를 누르기 전에 서야 하는 한 문장** (`UPLOAD_CLOSES_NOTICE`).
 *
 * ## ⭐ 한 작업 = 한 번 담기 = 한 번 결제 (2026-09-25 사용자 확정)
 *
 * 서버가 결제 뒤의 담기를 막는다(`UPLOAD_CLOSED_AFTER_PAYMENT`). ⛔ **막는 것만 있고 미리
 * 말하는 자리가 없으면 셀러는 누른 뒤에 안다.** 이 화면이 결제 직전의 마지막 자리다.
 *
 * ## ⭐ 정본 문장이 목업에 섰다 (2026-09-26 목업 `A-26`·`W-26`)
 *
 * 앱 `A-26` 과 글자가 같다. 그래서 상수를 `packages/shared/src/money-copy.ts` 로 올렸다
 * (`UPLOAD_CLOSES_NOTICE`). 첫 줄의 돈 문장(`PAYMENT_FAILED_IMAGE_NOTICE`)도 같은 자리에 있다.
 *
 * ⛔ **글자를 두 군데 적지 않는다.** 진짜 결제와 예시, 앱과 웹이 같은 사실을 다르게 말하면
 * 셀러는 다른 것으로 읽는다(용어 규칙). 한 상수를 넷이 함께 쓴다. */

export function PayStage() {
  // ⭐ **`상품 종류` 는 예시 값이 아니라 진짜 작업의 기록이다**(`A-26c` 와 같은 시점).
  const job = useOutletContext<ShellJob | undefined>();
  const { view } = useJobView(job?.id);

  return (
    <>
      {/* 옛 부제 「배경 지우기가 시작되기 전에 옵니다」는 화면의 자리를 설명하는 설계 메모라
          걷었다 (2026-10-04 사용자 지시). 제목 `결제` 는 그대로다. */}
      <PageHead frame="W-26" title="결제" />
      {/* ⛔ **`key` 를 빼지 마라.** `LivePay` 는 「결제창에서 돌아온 요청을 한 번만 읽었다」를
          `useRef` 로 들고 있어서, 작업을 옮겨 다닐 때 이 자리가 다시 서지 않으면 **그 표시가
          남은 채**로 다음 작업이 금액을 영영 못 받는다. 주소만 바뀌고 컴포넌트는 같은 자리다. */}
      {job ? <LivePay key={job.id} jobId={job.id} view={view} /> : null}
      {/* ⛔ 발밑 「웹은 인앱결제 강제가 없어 앱보다 경로가 단순합니다…」(미정 배지)를 걷었다
          (2026-10-04 사용자 지시). 셀러가 아니라 설계자에게 하는 말이었다. 결제 전 돈 문장은
          결제 버튼 바로 위 배너(공유 상수)가 말한다. */}
    </>
  );
}

/* ── 진짜 작업 ─────────────────────────────────────────────────────────────── */

/** 이 화면이 설 수 있는 자리. **셀러가 할 일이 자리마다 다르다.** */
type PayPhase =
  /** 금액을 아직 못 받았다. */
  | { kind: "LOADING" }
  /** 결제 전. 명세와 위젯이 선다. `points` 가 있으면 포인트 쓰기 칸도 선다(`null` = 칸 없음). */
  | { kind: "QUOTE"; quote: PaymentCheckoutView; points: PointBalanceView | null; notice: PayNotice | null }
  /** 결제할 수 없는 상태. **우리 쪽 준비가 안 됐거나 담긴 사진이 없다.** */
  | { kind: "BLOCKED"; message: PaymentMessage }
  /** 결제됨 · 아직 시작 전(`A-28`). `amount`(작업 전체)는 방금 결제한 경우에만 안다.
   *  `pointAmount` 는 그 가운데 포인트 몫이다(포인트를 안 썼거나 모르면 `null`). */
  | { kind: "PAID"; amount: number | null; methodLabel: string | null; pointAmount: number | null }
  /** 결제창에서 돌아온 승인을 서버에 확정하는 중이다. `message` 는 서버가 「확인 중」
   *  (409 `PAYMENT_IN_PROGRESS`)을 준 뒤에만 선다. 그 전에는 기존 「불러오는 중」 줄이다. */
  | { kind: "CONFIRMING"; message: PaymentMessage | null }
  /** 결제가 끝나지 못했다(`A-27`).
   *
   * `keyHeld` 는 **결제창에서 받은 결제 키를 아직 들고 있는가**다. 들고 있으면 `다시 시도` 가
   * 같은 키로 다시 확정하고, `카드 바꾸기`(새 결제위젯)는 닫힌다. 아래 `confirmKeyFate` 가 정한다. */
  | {
      kind: "FAILED";
      message: PaymentMessage;
      keyHeld: boolean;
      /** 결제창이 실패 코드를 달고 돌려보냈을 때만 선다. 셀러가 읽어 줄 수 있게 `실패 코드:` 로 보인다. */
      widgetCode?: string;
    };

/** 포인트 쪽 거절 뒤 금액을 다시 받아 결제 화면을 세울 때, 그 화면 맨 위에 서는 배너의 재료(목업 `W-26` 오른쪽
 *  「결제창에서 돌아왔는데 금액이 바뀌었을 때」 · 「카드 승인 뒤 되돌렸을 때」).
 *  `choice` 는 셀러가 앞서 고른 갈래다. 새 잔액으로도 서면 그대로 두고, 안 서면 고름을 비운다(저절로 다른 갈래를 고르지 않는다). */
type PayNotice =
  | { kind: "POINTS_CHANGED"; choice: WebPointChoice }
  | { kind: "POINTS_OFF" }
  | { kind: "CARD_REVERTED"; choice: WebPointChoice; lines: string[] };

/** 결제위젯 실패를 서버 로그로 보낸다(`docs/ERROR_LOGGING.md` §3-4-5). **셀러가 스스로 닫은 것은 보내지
 *  않는다**. 고칠 실패가 아니다. 한 번 쏘고 잊는다(`reportPaymentClientError`). */
function reportWidgetFailure(jobId: string, stage: PaymentWidgetStage, code: string): void {
  if (isWidgetSelfCancel(code)) return;
  reportPaymentClientError({ jobId, stage, code });
}

/** 위젯 실패 코드를 화면에 보이는가. **개발 빌드(`vite build --mode dev` · 로컬)에서만이다.**
 *
 * ⛔ **운영 셀러 화면에는 코드를 내지 않는다.** PRD `7.5` 「서버 에러 코드를 그대로 노출하지 않는다」가
 * 규칙이고, `7.5.9` 의 `실패 코드:` 표기는 **개발 화면의 예외**다. 운영에서 진단은 서버 로그가 맡는다
 * (`payment widget failed`. 보고는 빌드와 무관하게 늘 나간다. `docs/ERROR_LOGGING.md` §3-4-5). */
const SHOW_WIDGET_FAILURE_CODE = import.meta.env.MODE !== "production";

/** 위젯 실패 코드 한 줄. `7.5.9` 의 개발 화면 예외 표기 그대로다(`실패 코드:` + 고정폭). 위 상수가 꺼진
 *  빌드에서는 아무것도 그리지 않는다. ⛔ 문장을 더 붙이지 않는다. */
function WidgetFailureCode({ code }: { code: string }) {
  if (!SHOW_WIDGET_FAILURE_CODE) return null;
  return (
    <p className="hint">
      실패 코드: <span className="mono">{code}</span>
    </p>
  );
}

function LivePay({ jobId, view }: { jobId: string; view: DetailJobView | null }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  /** `진행 보기` 의 다시 읽기가 끝나면 그 작업으로 데려갈 것인가(`Uploading.tsx` 의 `follow` 와 같은 규율).
   *
   * ⛔ **화면을 떠나면 끈다.** 다시 읽기는 이 화면이 사라진 뒤에도 끝나고 `finally` 의 `navigate`
   * 도 그때 그대로 이동한다. 로그아웃 · 계정 전환이면 게이트가 데이터 클라이언트를 버리며 이
   * 다시 읽기를 취소하는데, 취소도 `finally` 를 부른다. 끄지 않으면 **다음 셀러를 앞 셀러의
   * 작업 주소로 데려간다**(서버는 404 지만 그 셀러가 거기서 시작할 이유가 없다). */
  const followProgress = useRef(false);
  useEffect(
    () => () => {
      followProgress.current = false;
    },
    [],
  );
  const [search, setSearch] = useSearchParams();
  const [phase, setPhase] = useState<PayPhase>({ kind: "LOADING" });

  /** 결제창에서 돌아온 요청을 **한 번만** 읽는다. 개발 모드의 이중 실행도 여기서 막힌다.
   *
   * ⭐ **2026-10-04. 결제 키는 승인이 판가름 날 때까지 주소에 남긴다.**
   *
   * 옛 판은 승인을 보내기 **전에** 주소를 비웠다. 그래서 서버가 409 `PAYMENT_IN_PROGRESS`
   * (작업 처리 잠금을 못 쥐어 **PG 를 부르지 않은** 경우. `apps/api/src/routes/payments.ts`)를
   * 주면 결제 키를 잃었고, `다시 시도` 가 새 결제위젯을 열어 토스 쪽 결제 세션이 만료된
   * 채(`NOT_FOUND_PAYMENT_SESSION`) 셀러가 결제를 처음부터 다시 해야 했다.
   *
   * 지금은 **판가름(승인됨 · 키가 끝났다)이 나면 그때 비운다.** 그 사이 새로 고침 · 뒤로 가기로
   * 같은 승인이 한 번 더 나가도 서버가 받아 준다: 같은 `paymentKey` 면 PG 를 부르지 않고 저장된
   * 결과를 돌려준다(`lib/checkout.ts` `confirmJobPayment` ③, 잠금을 못 쥔 갈래도 같은 키면
   * 저장된 결과. `routes/payments.ts` 승인 라우트). 그래서 「주소를 먼저 비운다」의 근거였던
   * 이중 승인은 서버가 막고, 주소를 남겨야만 키를 잃지 않는다. */
  const handled = useRef(false);

  /** 아직 판가름 나지 않은 승인 요청. `다시 시도` 가 **같은 키로** 다시 확정할 때 쓴다.
   *  포인트를 섞은 결제면 `pointAmount` 를 함께 든다(같은 키는 같은 두 몫으로만 다시 간다). */
  const pendingConfirm = useRef<ConfirmPaymentRequest | ConfirmWithPointsRequest | null>(null);

  /** 화면이 서 있는가. 자동 재확정의 대기가 화면을 벗어난 뒤에 이어지지 않게 하고,
   *  벗어난 뒤에 `setSearch` 가 셀러를 이 주소로 되끌어 오지 않게 한다. */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const loadQuote = useCallback(async (notice: PayNotice | null = null) => {
    setPhase({ kind: "LOADING" });
    // 잔액은 금액과 함께 받는다. ⛔ 잔액을 못 읽어도 결제는 막지 않는다(`fetchPointsForPay` 는 실패면 `null` = 칸 없음).
    const points = fetchPointsForPay();
    try {
      const quote = await fetchPaymentCheckout(jobId);
      setPhase({ kind: "QUOTE", quote, points: await points, notice });
    } catch (err) {
      // **이미 결제된 작업은 실패가 아니다.** 서버가 두 번째 위젯을 막은 것이고, 셀러가 할
      // 일은 시작하는 것이다(`A-28`). ⛔ 여기서 금액을 다시 내주지 않는 것이 서버 규약이라
      // **새로 들어온 셀러에게는 금액 줄이 없다.** 없는 수를 지어내지 않는다.
      if (err instanceof ApiError && err.code === "PAYMENT_ALREADY_PAID") {
        setPhase({ kind: "PAID", amount: null, methodLabel: null, pointAmount: null });
        return;
      }
      setPhase({ kind: "BLOCKED", message: paymentMessage(err) });
    }
  }, [jobId]);

  /** 승인을 확정한다. **서버가 「확인 중」(409 `PAYMENT_IN_PROGRESS`)을 주면 같은 키로 몇 번
   *  더 보낸다**(`confirmWithBusyRetry`. 간격과 상한은 아래 판정 구역).
   *
   * ⚠️ **호출 수**: 한 번 누를 때 최대 `CONFIRM_BUSY_RETRY_DELAYS_MS.length + 1`(=5)번이다.
   * 그 409 는 잠금을 못 쥔 갈래라 **PG 승인을 부르지 않는다.** 잠금을 쥐었는데 취소 표지가
   * 남아 409 가 나는 갈래는 PG 조회를 한 번 부른다(`cancelMarkerBlocks`). 그래서 최악이 PG
   * 조회 5번이고 승인은 키당 한 번이다(`claimPaymentKey`). 장당 원가(배경 지우기)는 들지 않는다.
   * ⛔ 상한을 풀거나 간격을 줄이지 마라. 이 자리가 무한 재시도가 되면 잠금을 쥔 쪽(취소 ·
   * 표지 맞추기)과 같은 PG 를 두드린다. */
  const runConfirm = useCallback(
    async (request: ConfirmPaymentRequest | ConfirmWithPointsRequest) => {
      pendingConfirm.current = request;
      setPhase({ kind: "CONFIRMING", message: null });
      // 포인트를 섞은 결제면 `amount` 는 토스 몫이고 포인트 몫이 따로 간다(`/api/payments/confirm-with-points`).
      const pointAmount = "pointAmount" in request ? request.pointAmount : null;
      try {
        // ⛔ **화면이 받은 셋을 그대로 넘긴다.** 여기서 다시 곱하거나 고치면 서버의 대조가
        //    뜻을 잃는다. 서버는 이 `amount` 를 믿지 않고 **다시 세어 맞대 본다.**
        const done = await confirmWithBusyRetry(
          () => ("pointAmount" in request ? confirmPaymentWithPoints(request) : confirmPayment(request)),
          describeConfirmFailure,
          (ms) => new Promise<boolean>((resolve) => window.setTimeout(() => resolve(alive.current), ms)),
          (err) => {
            if (alive.current) setPhase({ kind: "CONFIRMING", message: paymentMessage(err) });
          },
        );
        // ⭐ **승인이 섰다. 이제 키를 지운다.** 남기면 새로 고칠 때마다 같은 승인이 서버로 간다
        //    (서버가 저장된 결과를 돌려주니 돈은 안 움직이지만 할 일 없는 왕복이다).
        const after = confirmSettlement(alive.current, { ok: true });
        if (!after.render) return;
        if (!after.keepKey) pendingConfirm.current = null;
        if (after.clearAddress) setSearch({}, { replace: true });
        // 금액 줄은 작업 전체다(목업 `W-26` 결제됨: `₩4,000 결제됨` · `포인트 ₩3,000 · 신한 1234 ₩1,000`).
        setPhase({
          kind: "PAID",
          amount: done.amount + (pointAmount ?? 0),
          methodLabel: done.methodLabel,
          pointAmount,
        });
      } catch (err) {
        // 이 키로 더 할 일이 없을 때만(거절 · 금액 불일치 · 이미 결제됨 …) 지운다.
        const failure = describeConfirmFailure(err);
        const after = confirmSettlement(alive.current, {
          ok: false,
          failure,
        });
        if (!after.render) return;
        if (!after.keepKey) pendingConfirm.current = null;
        if (after.clearAddress) setSearch({}, { replace: true });
        // ⭐ 포인트 쪽 거절로 이 키가 끝났다(토스 전: 스위치 꺼짐 · 그사이 잔액이 바뀜 / 카드 승인 뒤: 카드를 되돌렸다).
        //    고장 난 갈래를 실패 패널로 세우지 않고 금액과 잔액을 다시 받아, 무슨 일이 있었는지 맨 위 배너로 말한다.
        const fallback = pointsConfirmFallback(failure);
        if (fallback !== null && !after.keepKey) {
          const reverted = cardRevertedLines(err);
          void loadQuote(
            fallback === "SPEND_OFF"
              ? { kind: "POINTS_OFF" }
              : fallback === "CARD_REVERTED" && reverted
                ? { kind: "CARD_REVERTED", choice: "POINTS_AND_TOSS", lines: reverted }
                : { kind: "POINTS_CHANGED", choice: "POINTS_AND_TOSS" },
          );
          return;
        }
        setPhase({ kind: "FAILED", message: paymentMessage(err), keyHeld: after.keepKey });
      }
    },
    [loadQuote, setSearch],
  );

  useEffect(() => {
    if (handled.current) return;
    const back = readWidgetReturn(search);

    if (back.kind === "NONE") {
      handled.current = true;
      void loadQuote();
      return;
    }

    handled.current = true;

    if (back.kind === "FAIL") {
      // 결제창이 실패로 돌려보낸 주소에는 결제 키가 없다. 남길 것이 없어 바로 비운다.
      setSearch({}, { replace: true });
      // ⛔ 주소의 `message` 는 읽지도 보내지도 않는다. 코드만 같은 규칙으로 걸러 쓴다.
      const code = paymentClientErrorCode(back.code);
      reportWidgetFailure(jobId, "return", code);
      setPhase({
        kind: "FAILED",
        message: widgetFailureMessage(code),
        keyHeld: false,
        ...(isWidgetSelfCancel(code) ? {} : { widgetCode: code }),
      });
      return;
    }

    // ⛔ **여기서 주소를 비우지 않는다**(위 `handled` 머리말). `runConfirm` 이 판가름 뒤에 비운다.
    // 포인트를 섞어 결제창에 갔으면 성공 주소에 포인트 몫이 실려 돌아온다(`withReturnPointAmount`).
    const pointAmount = readReturnPointAmount(search);
    void runConfirm({
      paymentKey: back.paymentKey,
      orderId: back.orderId,
      amount: back.amount,
      ...(pointAmount !== null ? { pointAmount } : {}),
    });
  }, [jobId, loadQuote, runConfirm, search, setSearch]);

  /** `다시 시도`. **들고 있는 키가 있으면 같은 키로 확정**하고, 없으면 결제위젯을 새로 연다. */
  const retry = useCallback(() => {
    const held = pendingConfirm.current;
    if (paymentActionOf("RETRY", held !== null) === "RECONFIRM" && held) {
      void runConfirm(held);
      return;
    }
    void loadQuote();
  }, [loadQuote, runConfirm]);

  /** `카드 바꾸기`. 새 결제위젯을 연다. ⛔ **키를 들고 있을 때는 열지 않는다**(`paymentActionOf`).
   *  판가름 안 난 승인을 두고 새 결제를 열면 같은 작업에 승인이 둘 걸릴 수 있다. */
  const otherCard = useCallback(() => {
    if (paymentActionOf("OTHER_CARD", pendingConfirm.current !== null) !== "NEW_WIDGET") return;
    void loadQuote();
  }, [loadQuote]);

  /** `진행 보기`. 작업을 다시 읽고 **그 작업의 지금 단계 화면**으로 간다(`/jobs/:id` 가
   *  상태로 화면을 고른다. `App.tsx` `StageIndex`).
   *
   * 다시 읽는 까닭: 폰에서 시작한 작업인데 캐시가 결제 단계로 들고 있으면 `/pay` 로 되돌아온다.
   * ⚠️ **아직 결제 단계인 작업은 다시 읽어도 `/pay` 로 돌아온다.** 그때 질의 문자열이 빠지고
   * 화면이 새로 서서 결제 키 없이 새 결제위젯이 뜬다. 그래서 키를 들고 있는 동안은 닫는다
   * (`paymentActionOf` · 아래 버튼의 `disabled`). */
  const viewProgress = useCallback(() => {
    if (paymentActionOf("VIEW_PROGRESS", pendingConfirm.current !== null) !== "NAVIGATE") return;
    followProgress.current = true;
    void queryClient
      .refetchQueries({ queryKey: ["job", jobId] })
      .finally(() => settleFollow(followProgress, () => navigate(`/jobs/${jobId}`)));
  }, [jobId, navigate, queryClient]);

  const keyHeld = holdsPaymentKey(phase);
  /** 키를 들고 있어 닫힌 버튼의 사유. 서버 응답의 기존 문장을 쓰고 새로 짓지 않는다. 확정을
   *  막 보낸 참(문장이 아직 없다)에는 왼쪽 「불러오는 중」 줄이 그 사유다. */
  const heldReason =
    phase.kind === "FAILED" || phase.kind === "CONFIRMING" ? (phase.message?.text ?? null) : null;

  return (
    <div className="review" style={{ marginTop: 16 }}>
      <div>
        {phase.kind === "QUOTE" ? (
          <PayForm
            jobId={jobId}
            quote={phase.quote}
            points={phase.points}
            notice={phase.notice}
            onPointsPaid={(amount) => setPhase({ kind: "PAID", amount, methodLabel: null, pointAmount: amount })}
            onAlreadyPaid={() => setPhase({ kind: "PAID", amount: null, methodLabel: null, pointAmount: null })}
            onReload={(notice) => void loadQuote(notice)}
          />
        ) : null}
        {phase.kind === "LOADING" || (phase.kind === "CONFIRMING" && !phase.message) ? (
          <p className="hint">금액을 불러오는 중입니다.</p>
        ) : null}
        {/* 「확인 중」은 서버 응답의 문구 그대로다(`payment-messages.ts` 의 `PAYMENT_IN_PROGRESS`).
            ⛔ 이 자리를 위해 새 문장을 짓지 않는다. 같은 키로 다시 보내는 동안 버튼은 없다. */}
        {phase.kind === "CONFIRMING" && phase.message ? (
          <p className="hint" role="status">
            {phase.message.text}
          </p>
        ) : null}
        {phase.kind === "BLOCKED" ? <BlockedPanel message={phase.message} /> : null}
        {phase.kind === "PAID" ? (
          <PaidPanel
            jobId={jobId}
            view={view}
            amount={phase.amount}
            methodLabel={phase.methodLabel}
            pointAmount={phase.pointAmount}
            onStarted={() => navigate(`/jobs/${jobId}`)}
          />
        ) : null}
        {phase.kind === "FAILED" ? (
          <FailedPanel
            message={phase.message}
            keyHeld={phase.keyHeld}
            widgetCode={phase.widgetCode ?? null}
            onRetry={retry}
            onOtherCard={otherCard}
          />
        ) : null}
      </div>

      <div className="editpanel">
        {/* ⭐ `W-26` 의 `상품 종류`. **폰은 시트를 올리고(`A-26c`) 웹은 여기 펴 둔다.**
            ⛔ **한쪽만 받으면 「어디서 결제했느냐에 따라 기록이 있기도 없기도」 한다.**
            문구와 고를 값은 앱과 **같은 상수**에서 온다 — 여기서 다시 적지 않는다. */}
        <ProductCategorySection jobId={jobId} view={view} />

        <section>
          <h3>폰에서 이미 시작했을 때</h3>
          <LimitBanner>
            <span className="l1">같은 장을 두 번 만들면 같은 장에 두 번 금액이 나갑니다.</span>
            <span className="l2">그래서 여기서 막습니다. 두 번 시작할 수 없습니다.</span>
          </LimitBanner>
          {/* ⭐ **막는 자리가 서버다.** 작업 잠금(`claimJobForProcessing`)이 한 작업에 도는
              처리를 하나로 묶으므로, 폰에서 시작한 작업에 여기서 또 걸면 409 가 온다. */}
          <p className="hint">
            폰에서 이미 시작하셨으면 여기서 또 시작해도 같은 작업이 두 번 만들어지지 않습니다.
          </p>
          {/* ⭐ 목업 `W-26` 이 상태의 주 동작 `진행 보기`. 셀러가 할 일은 다시 시작이 아니라
              진행을 보는 것이다. 목업의 꺼진 `바로 시작` 은 옮기지 않았다. 진짜 작업에서는 이
              자리가 「이미 시작했다」를 판정하지 않으므로, 꺼 두고 「폰에서 이미 시작했습니다」를
              달면 시작 전 작업에 거짓을 말한다. 시작은 왼쪽 `바로 시작` 하나다. */}
          <div style={{ display: "flex", gap: 8, marginTop: 9, flexWrap: "wrap-reverse" }}>
            <Button
              label="진행 보기"
              small
              disabled={keyHeld}
              {...(keyHeld && heldReason ? { disabledReason: heldReason } : {})}
              onClick={viewProgress}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

/** 결제 전. **명세 · 결제위젯 · 버튼 줄.** */
function PayForm({
  jobId,
  quote,
  points,
  notice,
  onPointsPaid,
  onAlreadyPaid,
  onReload,
}: {
  jobId: string;
  // ⛔ 작업 화면값(`view`)을 받지 않는다. 여기서 쓰던 유일한 자리가 주문명이었고, 그 자리에
  //    셀러 상품명이 실리던 것이 결함이었다. 다시 받으면 같은 길이 다시 열린다.
  quote: PaymentCheckoutView;
  /** 잔액. `null` 이면 포인트 쓰기 칸이 없고 지금까지의 결제 그대로다. */
  points: PointBalanceView | null;
  /** 포인트 쪽 거절 뒤 다시 세운 화면이면 맨 위 배너의 재료. */
  notice: PayNotice | null;
  /** 포인트로만 낸 결제가 섰다. 인자는 작업 전체 금액(= 포인트 몫)이다. */
  onPointsPaid: (amount: number) => void;
  onAlreadyPaid: () => void;
  /** 금액과 잔액을 다시 받는다(포인트 거절 뒤). 배너 재료를 함께 넘긴다. */
  onReload: (notice: PayNotice) => void;
}) {
  const [confirmQuit, setConfirmQuit] = useState(false);
  /** 서버가 SPEND 와 같은 꺼내는 순서로 센 「이 결제가 쓰는 포인트」와 그 한 줄(법무 `L-F79`-7). ⛔ **이 줄이 서지 않으면 칸을
   *  세우지 않는다.** 스위치가 꺼졌으면 서버가 `pointUse` 를 싣지 않아 여기서 `null` 이 된다. */
  const use = pointUseFor(quote.amount, points, checkoutPointUse(quote));
  const useLine = use ? pointUseLine(use, localMonthDay) : null;
  /** 칸을 세울 잔액. 위 줄이 섰을 때만 잔액을 쓴다. 아니면 `null` = 지금까지의 결제 그대로. */
  const usable = useLine !== null ? points : null;
  /** 포인트 쓰기 칸에서 고른 갈래. ⛔ **처음에는 아무것도 골라져 있지 않다**(목업 `W-26`). */
  const [choice, setChoice] = useState<WebPointChoice | null>(() => carriedChoice(quote.amount, usable, notice));
  const offered = pointsOffered(usable);
  // ⭐ 두 몫은 공유 `planWebPointSplit` 이 정한다. 서버가 승인 때 같은 함수로 다시 세어 맞댄다.
  const split = planForPay(quote.amount, usable, choice);
  /** 포인트로만이면 결제위젯이 서지 않는다(목업 `W-26p` 「위젯 자리가 비고 결제창으로 넘어가지 않습니다」). */
  const pointsOnly = split !== null && split.tossAmount === 0;
  /** 포인트로만 내기가 실패했을 때 보이는 문장(결제 문장 표의 것). */
  const [pointsFailure, setPointsFailure] = useState<PaymentMessage | null>(null);
  const [widget, setWidget] = useState<PaymentWidgetHandle | null>(null);
  /** 위젯을 못 띄운 단계와 코드. 비어 있지 않으면 `결제창을 불러오지 못했습니다` 와 `실패 코드:` 가 선다. */
  const [loadFailure, setLoadFailure] = useState<PaymentWidgetError | null>(null);
  const loadFailed = loadFailure !== null;
  /** 결제창으로 못 보낸 코드(`requestPayment` 실패). 셀러가 스스로 닫은 것은 담지 않는다. */
  const [requestFailure, setRequestFailure] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  // ⛔ **`quote.amount` 를 화면이 다시 곱하지 않는다.** 서버가 정한 수 그대로다.
  useEffect(() => {
    let alive = true;
    let mounted: PaymentWidgetHandle | null = null;
    setLoadFailure(null);

    void (async () => {
      try {
        const handle = await mountPaymentWidget({
          clientKey: quote.clientKey,
          amount: quote.amount,
          methodsSelector: "#toss-payment-method",
          agreementSelector: "#toss-payment-agreement",
        });
        mounted = handle;
        // 화면을 벗어난 뒤에 붙는 것을 막는다. 붙으면 떼는 사람이 없다.
        if (!alive) {
          void handle.destroy();
          return;
        }
        setWidget(handle);
      } catch (err) {
        // ⛔ **삼키지 않는다.** 조용히 빈 자리로 두면 셀러는 결제창이 뜨기를 기다린다.
        if (!alive) return;
        // 단계가 안 붙은 실패(우리 코드의 예외)도 「불러오지 못했다」로 말하고 `UNKNOWN` 으로 보고한다.
        const failure = err instanceof PaymentWidgetError ? err : new PaymentWidgetError("load", "UNKNOWN");
        if (!(err instanceof PaymentWidgetError)) console.error("payment widget mount failed");
        setLoadFailure(failure);
        reportWidgetFailure(jobId, failure.stage, failure.code);
      }
    })();

    return () => {
      alive = false;
      setWidget(null);
      if (mounted) void mounted.destroy();
    };
  }, [jobId, quote.amount, quote.clientKey]);

  /** 위젯이 받을 금액 = 토스 몫. 아직 안 골랐으면 작업 전체(위젯이 처음 선 금액)다. */
  const tossAmount = split?.tossAmount ?? quote.amount;
  // 고른 갈래가 바뀌면 위젯 금액을 토스 몫으로 올린다(목업 `W-26p`: 「아래 결제 방법으로」가 위젯과 결제창에 뜨는 금액).
  // 여기서는 보여 주기만 한다. 실패는 `pay()` 가 결제창으로 가기 직전에 같은 금액을 다시 올리며 잡아 셀러에게 말한다.
  useEffect(() => {
    if (!widget || tossAmount <= 0) return;
    void widget.setAmount(tossAmount).catch(() => undefined);
  }, [widget, tossAmount]);

  /** 포인트로만. 토스를 부르지 않고 그 자리에서 포인트가 빠진다(앱 `A-26` 과 같음). */
  async function payWithPointsOnly() {
    setSending(true);
    setPointsFailure(null);
    try {
      await payJobWithPoints(jobId);
      onPointsPaid(quote.amount);
    } catch (err) {
      setSending(false);
      const outcome = pointsPayOutcome(err instanceof ApiError ? (err.code ?? null) : null);
      // ⛔ 스위치가 꺼졌으면 칸을 숨긴다. 고장 난 갈래를 남겨 두지 않는다. 잔액이 그사이 바뀌었으면 새 숫자로 다시 세운다.
      if (outcome === "SPEND_OFF") {
        // 다시 받는 결제 준비에는 `pointUse` 가 없어 칸이 서지 않는다(서버가 스위치를 본다).
        onReload({ kind: "POINTS_OFF" });
        return;
      }
      if (outcome === "RELOAD") {
        onReload({ kind: "POINTS_CHANGED", choice: "POINTS_ONLY" });
        return;
      }
      if (outcome === "ALREADY_PAID") {
        onAlreadyPaid();
        return;
      }
      setPointsFailure(paymentMessage(err));
    }
  }

  async function pay() {
    if (sending || split === null) return;
    if (split.tossAmount === 0) {
      await payWithPointsOnly();
      return;
    }
    if (!widget) return;
    setSending(true);
    setRequestFailure(null);
    try {
      // ⭐ 결제창으로 가기 직전에 위젯 금액을 이번 토스 몫으로 다시 맞춘다. 위 효과가 실패했거나 늦었어도 여기서 선다.
      await widget.setAmount(split.tossAmount);
      await widget.requestPayment({
        orderId: quote.orderId,
        // 결제창과 카드 명세에 찍히는 이름이다. ⛔ **셀러 상품명을 넣지 않는다.** 넣으면 셀러
        // 상품을 우리가 판 것처럼 찍히고 셀러 영업 정보가 결제사로 나간다(L-F55·L-F53 축).
        // 우리가 파는 서비스 이름과 장수만 적는다. 문구는 앱과 같은 shared 함수에서 나온다.
        orderName: paymentOrderName(quote.quantity),
        // 포인트를 섞으면 포인트 몫을 성공 주소에 실어 보낸다. 돌아와서 `confirm-with-points` 로 확정한다.
        successUrl:
          split.pointAmount > 0
            ? withReturnPointAmount(widgetReturnUrl(jobId), split.pointAmount)
            : widgetReturnUrl(jobId),
        failUrl: widgetReturnUrl(jobId),
      });
    } catch (err) {
      // 결제창이 안 열렸다. **돈은 움직이지 않았다.** 셀러가 다시 누를 수 있게 열어 둔다.
      setSending(false);
      const code = err instanceof PaymentWidgetError ? err.code : "UNKNOWN";
      // 셀러가 결제창을 스스로 닫은 것은 지금처럼 조용히 둔다(문장도 보고도 없다).
      if (isWidgetSelfCancel(code)) return;
      setRequestFailure(code);
      reportWidgetFailure(jobId, "request", code);
    }
  }

  /** 맨 위 배너(포인트 쪽 거절 뒤 다시 세운 화면). 금액은 다시 받은 잔액과 지금 고름으로 다시 센다. */
  const noticeLines =
    notice === null
      ? null
      : notice.kind === "CARD_REVERTED"
        ? notice.lines
        : pointsAdjustedLines(notice.kind === "POINTS_OFF" ? "OFF" : "CHANGED", split);

  return (
    <>
      {noticeLines ? (
        // 목업 `W-26` 오른쪽: 금액이 바뀐 갈래는 `!`, 카드를 되돌린 갈래는 `₩` 배너다. 결제하지 않고 이 화면으로 돌아왔다는 사실을
        // 맨 위에서 말한다. ⛔ 조용히 다시 세우면 결제창 인증을 마친 셀러가 왜 같은 화면인지 모른다.
        <div style={{ marginBottom: 12 }}>
          <LimitBanner icon={notice?.kind === "CARD_REVERTED" ? "₩" : "!"}>
            {noticeLines.map((line, i) => (
              <span key={line} className={i === 0 ? "l1" : "l2"}>
                {line}
              </span>
            ))}
          </LimitBanner>
        </div>
      ) : null}
      {/* **제목 배지를 뗐다(2026-09-13).** 장당 청구가가 확정돼 재산정이 끝났다. 부가세 포함
          여부도 2026-09-14 O-44 로 「부가세 포함」이 확정돼 단가 줄의 `부가세 포함 미정` 배지를
          걷었다. 그 말은 아래 **단가 줄 하나**가 진다. 앱 `A-26` 과 같은 상수다. */}
      <SettingGroup bill title="이번 작업">
        <SettingRow label="배경을 지울 사진" value={`${quote.quantity}장`} />
        <SettingRow
          label={UNIT_PRICE_ROW_LABEL}
          value={formatKrw(quote.unitPriceAmount)}
        />
        <SettingRow label="합계" value={formatKrw(quote.amount)} tone="total" />
      </SettingGroup>

      {/* ⭐ 포인트 쓰기 칸(목업 `W-26` · `W-26p`). 잔액이 있고 쓸 수 있는 셀러에게만 선다. 잔액 `₩0` 이거나 스위치가
          꺼져 있으면 칸이 없고 지금까지의 결제 그대로다. */}
      {offered ? (
        <PointChoice
          total={quote.amount}
          points={usable}
          useLine={useLine ?? ""}
          choice={choice}
          split={split}
          disabled={sending}
          onChoose={(next) => {
            setChoice(next);
            // 앞 갈래의 실패 문장을 남기지 않는다(결제창을 못 연 줄은 포인트로만에서 뜻이 없다).
            setPointsFailure(null);
            setRequestFailure(null);
          }}
        />
      ) : null}

      {/* ⭐ **결제 수단과 약관 동의는 결제사가 그린다.** 우리가 그리면 카드사 규칙이 바뀔 때마다
          우리 재배포를 기다린다(`lib/toss-widget.ts`). 이 두 칸은 자리만 내준다.
          포인트로만이면 자리를 비운다. 위젯은 떼지 않고 숨긴다(다른 갈래로 바꾸면 그대로 다시 보인다). */}
      <div style={{ marginTop: 12, ...(pointsOnly ? { display: "none" } : {}) }}>
        <div id="toss-payment-method" />
        <div id="toss-payment-agreement" />
      </div>

      {loadFailed && !pointsOnly ? (
        // ⛔ **비활성 사유를 툴팁이 아니라 항상 보이는 글로 둔다**(`A-28b`·`A-25c` 규격).
        <>
          <p className="hint" role="status">
            결제창을 불러오지 못했습니다. 연결을 확인하고 이 화면을 새로 열어 주세요.
          </p>
          <WidgetFailureCode code={loadFailure.code} />
        </>
      ) : null}
      {requestFailure ? (
        // 결제창이 **열리지도 못했다.** ⛔ `widgetFailureMessage` 를 쓰지 않는다. 그 기본 문장은 카드 거절
        // (`PAYMENT_DECLINED`)로 읽혀 셀러가 카드를 의심한다. 「못 열었다」를 말하는 기존 문장을 그대로 쓴다.
        <>
          <p className="hint" role="status">
            결제창을 불러오지 못했습니다. 연결을 확인하고 이 화면을 새로 열어 주세요.
          </p>
          <WidgetFailureCode code={requestFailure} />
        </>
      ) : null}

      {/* ⭐ **배너는 위젯 아래, `결제하고 시작` 바로 위다**(목업 `W-26p`, 2026-10-03 셋째 회차).
          위젯 위에 두면 셀러가 카드를 고르는 사이 화면 위로 밀려나 누르는 순간에 안 보인다.
          위젯 안은 결제사 몫이라 우리 문장을 넣을 수 없다. 목업 `W-26p` 프레임은 마지막 두 줄만
          그렸지만, 앞 두 줄(돈 · 담기가 끝난다)도 누르기 전에 알아야 하는 사실이라 함께 옮겼다. */}
      <div style={{ marginTop: 12 }}>
        {/* 낱말은 용어 표(PRD 7.5.9)를 따른다. `환불`은 셀러 화면에서 쓰지 않는다. */}
        <LimitBanner>
          {/* ⭐ **목업 `W-26` 글자 그대로**(앱 `A-26` 과 같은 상수. 2026-10-02). 옛 판의 `못 만든
              장은 금액이 돌아갑니다` 는 2026-09-23 확정 전의 말이다. 이제 못 만든 장 금액은
              다시 만들면 그 장에 쓰이고, 다시 만들지 않으면 돌아간다(시점은 환불정책 제4조).
              ⚠️ 옛 셋째 줄의 `그만두면 올린 사진이 지워집니다.` 는 목업에 없어 걷었다. 그 사실은
              `그만두기` 아래에서 제자리로 열리는 확인(`A-26b`)이 누르기 전에 말한다. */}
          <span className="l1">{PAYMENT_FAILED_IMAGE_NOTICE}</span>
          {/* ⛔ **누른 뒤에 알면 늦는 사실이다.** 서버가 결제 뒤의 담기를 막으므로
              (`UPLOAD_CLOSED_AFTER_PAYMENT`) 이 줄이 없으면 셀러는 막힌 다음에 안다. */}
          <span className="l2">{UPLOAD_CLOSES_NOTICE}</span>
          <span className="l2">
            {CANCEL_BEFORE_START_NOTICE}
          </span>
          {/* ⛔⛔ **결제 버튼 앞에 서야 하는 제한이다**(목업 `W-26p` 2026-10-03 셋째 회차 · 법무
              `6A.14`(2)ⓕ · `L-F52`). 결제가 끝난 뒤에야 나오면 셀러가 받아들인 내용이 아니었다고
              다투어질 여지가 있다. `P-02` 제2조 2 · 앱 `A-26` 과 같은 상수다.
              2026-10-04 「변호사 확인 없이 연다」 사용자 결정으로 `확인 조치 미정` 배지를 걷었다(법무
              `6A.15`(8)ⓓ③. 공개 환불정책과 같은 상태로 선다). 체크 칸을 짓지 않고 위젯의 약관 동의와
              섞지 않는다. 바로 위 시작 전 취소 줄의 `동의 절차 미정` 도 같은 원칙으로 걷었다(PRD `7.0`
              「로그인 전 화면의 확정 문구」 판단 4). */}
          <span className="l2">
            {MADE_SHEET_NO_REFUND_NOTICE}
          </span>
        </LimitBanner>
      </div>

      <div className={`actions${confirmQuit ? " dropopen" : ""}`}>
        {/* 금액은 버튼 안이 아니라 버튼 바로 위·옆 한 줄이다(PRD 7.5.2). */}
        <span style={{ flex: "none" }}>
          <PayNow label="지금 결제할 금액" amount={quote.amount} />
        </span>
        {/* 그만두기 확인은 버튼 아래에서 제자리로 연다. 폰은 시트가 올라온다. */}
        <span className="dropwrap">
          <Button
            label="그만두기"
            variant="ghost"
            small
            aria-expanded={confirmQuit}
            onClick={() => setConfirmQuit((v) => !v)}
          />
          {confirmQuit ? (
            <span className="webdrop fromleft" role="group" aria-label="그만두기 확인">
              <span className="dh">올린 사진이 지워집니다</span>
              {/* 두 문장 48자 줄글이라 어절 보존을 걷는다(`styles/base.css` 의 `.prose`).
                  앱의 그만두기 시트가 같은 말을 한다. 한쪽만 걷으면 한 문구가 폰과 PC 에서
                  다르게 갈린다. 아래 예시 판(`.dn`)도 같이 붙여 둔다. */}
              <span className="dn prose">
                결제 전에 그만두면 올린 {quote.quantity}장은 남기지 않습니다. 다시 하려면 처음부터
                올려야 합니다.
              </span>
              <span className="drow">
                <Button
                  label="계속하기"
                  variant="ghost"
                  small
                  onClick={() => setConfirmQuit(false)}
                />
                <Button
                  label="그만두기"
                  variant="danger"
                  small
                  disabled
                  // ⚠️ **사유가 2026-09-25에 바뀌었다.** 결제는 붙었고, 없는 것은 **올린
                  //    사진을 지우는 자리**다. 낡은 사유를 두면 셀러가 결제를 의심한다.
                  disabledReason="올린 사진을 지우는 자리가 아직 없습니다."
                />
              </span>
            </span>
          ) : null}
        </span>
        <Button
          label="결제하고 시작"
          small
          disabled={split === null || (!pointsOnly && !widget) || sending}
          {...(split === null
            ? // 목업 `W-26`: 고르기 전에는 꺼지고 이 말이 버튼 위에 항상 보인다.
              { disabledReason: "포인트를 쓸지 골라 주세요" }
            : pointsOnly || widget
              ? {}
              : {
                  disabledReason: loadFailed
                    ? "결제창을 불러오지 못했습니다."
                    : "결제창을 불러오는 중입니다.",
                })}
          onClick={() => void pay()}
        />
      </div>
      {pointsFailure ? (
        <p className="hint" role="status">
          {pointsFailure.text}
        </p>
      ) : null}
    </>
  );
}

/** 결제할 수 없는 자리. **우리 쪽 준비가 안 됐거나 담긴 사진이 없다.**
 *
 * ⚠️ **오늘 가장 자주 서는 자리다.** `TOSS_CLIENT_KEY` 가 비어 있어 서버가 503 을 낸다.
 * 그 사실을 화면이 또렷하게 말하지 않으면 셀러는 카드번호를 넣을 자리를 찾는다. */
function BlockedPanel({ message }: { message: PaymentMessage }) {
  return (
    <>
      <EmptyState art="₩" tone="bad" title="지금은 결제할 수 없어요" body={message.text} />
      <LimitBanner icon="✓">올린 사진은 그대로 있습니다.</LimitBanner>
      <div className="actions">
        <Button
          label="결제하고 시작"
          small
          disabled
          // ⛔ 사유를 툴팁이 아니라 항상 보이는 글로 둔다. 눌러 보기 전에 알아야 한다.
          disabledReason={message.text}
        />
      </div>
    </>
  );
}

/** `A-27` 결제가 끝나지 못했을 때. 목업 `W-26` 실패 상태의 버튼 둘(`카드 바꾸기` · `다시 시도`).
 *
 * - `다시 시도`: 결제 키를 들고 있으면 **같은 키로** 다시 확정하고, 아니면 결제위젯을 새로 연다.
 * - `카드 바꾸기`: 결제위젯을 새로 연다. ⛔ **키를 들고 있으면 닫는다.** 판가름 안 난 승인
 *   (확인 중 · 연결 끊김 · 모름)을 두고 새 결제를 열면 같은 작업에 승인이 둘 걸릴 수 있다.
 *
 * ⛔ 두 버튼 모두 `retryable` 이 거짓인 갈래(돈이 움직였는지 모르는 경우 등)에는 안 연다.
 * 꺼진 사유는 항상 보이는 글로 두되, **같은 문장을 두 번 찍지 않는다.** `다시 시도` 가
 * 꺼져 있으면 그쪽이 사유를 말하고, `다시 시도` 만 열려 있을 때 `카드 바꾸기` 가 말한다. */
function FailedPanel({
  message,
  keyHeld,
  widgetCode,
  onRetry,
  onOtherCard,
}: {
  message: PaymentMessage;
  keyHeld: boolean;
  widgetCode: string | null;
  onRetry: () => void;
  onOtherCard: () => void;
}) {
  const otherCardOpen = message.retryable && paymentActionOf("OTHER_CARD", keyHeld) === "NEW_WIDGET";
  return (
    <>
      <EmptyState art="!" tone="bad" title="결제가 끝나지 않았어요" body={message.text} />
      {widgetCode ? <WidgetFailureCode code={widgetCode} /> : null}
      <LimitBanner icon="✓">
        올린 사진은 그대로 있습니다. 결제만 되면 그 자리에서 이어서 시작합니다.
      </LimitBanner>
      <div className="actions">
        <Button
          label="카드 바꾸기"
          variant="ghost"
          small
          disabled={!otherCardOpen}
          {...(otherCardOpen || !message.retryable ? {} : { disabledReason: message.text })}
          onClick={onOtherCard}
        />
        {/* ⛔ **셀러가 누를 때만 다시 간다.** 자동으로 다시 보내는 것은 「확인 중」 하나뿐이고
            그것도 상한이 있다(`LivePay` 의 `runConfirm`). */}
        <Button
          label="다시 시도"
          small
          disabled={!message.retryable}
          {...(message.retryable ? {} : { disabledReason: message.text })}
          onClick={onRetry}
        />
      </div>
    </>
  );
}

/** `A-28` 결제됨 · 아직 시작 전. **여기서 배경 지우기를 시작한다.**
 *
 * ⛔ **승인이 끝나자마자 자동으로 시작하지 않는다.** 시작 전이 곧 전액이 돌아가는 구간이라
 * (위 제한 배너가 그렇게 약속했다) 건너뛰면 **그 약속이 말뿐이 된다.** 누르는 것은 셀러다. */
function PaidPanel({
  jobId,
  view,
  amount,
  methodLabel,
  pointAmount,
  onStarted,
}: {
  jobId: string;
  view: DetailJobView | null;
  amount: number | null;
  methodLabel: string | null;
  /** 그 결제의 포인트 몫. 포인트를 안 썼거나 모르면 `null`. */
  pointAmount: number | null;
  onStarted: () => void;
}) {
  const queryClient = useQueryClient();
  const [starting, setStarting] = useState(false);
  const [failure, setFailure] = useState<PaymentMessage | null>(null);

  async function start() {
    if (starting) return;
    setStarting(true);
    setFailure(null);
    try {
      const next = await processJob(jobId);
      queryClient.setQueryData(["job", jobId], next);
      onStarted();
    } catch (err) {
      // ⛔ **`ALREADY_PROCESSING` 은 실패가 아니다.** 폰에서 이미 시작한 작업이다. 다시
      //    누를 자리를 열어 주면 안 되고, 셀러가 할 일은 진행을 보는 것이다.
      if (err instanceof ApiError && err.code === "ALREADY_PROCESSING") {
        onStarted();
        return;
      }
      setFailure(paymentMessage(err));
      setStarting(false);
    }
  }

  return (
    <>
      <div className="push">
        <span className="ico" aria-hidden="true">
          ✓
        </span>
        <span>
          {/* 금액은 **방금 승인받은 경우에만** 안다. 모르는 수를 지어내지 않는다. */}
          <span className="h">{amount === null ? "결제되었습니다" : `${formatKrw(amount)} 결제됨`}</span>
          <span className="b">
            {[view?.productName, ...paidMethodParts(amount, methodLabel, pointAmount)].filter(Boolean).join(" · ") ||
              "배경 지우기"}
          </span>
        </span>
      </div>
      <div style={{ marginTop: 9 }}>
        <LimitBanner icon="₩">
          {/* 시작 전 취소는 수동이다(환불정책 제2조 1, 2026-10-04 사용자 결정 B안). 셀러가 할 일은
              연락처로 알리는 것이라 그 연락처를 이 자리에서 바로 보인다(법무 `L-F51`-4). 문구 정본은
              PRD `7.0` 「로그인 전 화면의 확정 문구」 (5) 4a. */}
          <span className="l1">
            {CANCEL_BEFORE_START_NOTICE} 연락처는 <span className="mono">{CONTACT_EMAIL}</span>입니다.
          </span>
          <span className="l2">시작된 뒤에는 전액 취소가 되지 않습니다. {MADE_SHEET_NO_REFUND_NOTICE}</span>
        </LimitBanner>
      </div>
      {/* 버튼 라벨에 돈 낱말을 섞지 않는다. 돈은 바로 위 배너가 말한다(PRD 7.5.9). */}
      <div className="actions">
        <Button
          label="취소하기"
          variant="ghost"
          small
          disabled
          // ⚠️ 여기서 결제를 취소하는 길은 없다(금액을 돌려보내는 코드 0건). **숨기지 않는다.** 숨기면
          //    셀러는 없는 버튼을 찾는다. 대신 사유가 위 배너의 연락처로 잇는다(법무 `L-F51`-4).
          disabledReason="여기서는 취소할 수 없습니다. 위 연락처로 알려 주세요."
        />
        <Button label="바로 시작" small disabled={starting} onClick={() => void start()} />
      </div>
      {failure ? (
        <p className="hint" role="status">
          {failure.text}
        </p>
      ) : null}
    </>
  );
}

/* ── 판정 시작 (pay-confirm-rules) ───────────────────────────────────────────
 *
 * ⛔ **이 구역은 import 를 쓰지 않는다. JSX 도 쓰지 않는다.** `scripts/web-pay-confirm.test.mjs`
 * 가 이 구역만 잘라 타입을 걷고 실제로 불러 본다. 돈이 걸린 판단(같은 키로 다시 보낼지,
 * 키를 들고 있을지)이 컴포넌트 안에 섞여 있으면 시험이 글자만 보고 동작은 못 본다. */

/** 서버가 「확인 중」(409 `PAYMENT_IN_PROGRESS`)을 줄 때 같은 키로 다시 보내기 전의 대기.
 *
 * **길이가 곧 상한이다**: 첫 요청 + 4번 = 최대 5번, 다 기다리면 약 15초다. 토스는 결제창
 * 인증 뒤 **10분 안에** 승인 API 를 불러야 하고 넘기면 세션이 만료된다
 * (`NOT_FOUND_PAYMENT_SESSION`. 토스페이먼츠 결제위젯 연동 문서의 승인 절). 15초는 그
 * 안에서 넉넉하고, 그 뒤에도 키는 주소에 남아 셀러가 `다시 시도` 로 같은 키를 다시 보낼 수
 * 있다. ⛔ 늘리지 마라. 셀러는 그동안 버튼 없는 화면을 본다. */
export const CONFIRM_BUSY_RETRY_DELAYS_MS: readonly number[] = [1_000, 2_000, 4_000, 8_000];

/** 승인 실패를 판정에 필요한 둘만 남긴 모양. 서버 응답 본문은 싣지 않는다. */
export interface ConfirmFailure {
  /** HTTP 상태. `0` 은 응답을 못 받은 것이다(`lib/api.ts` 의 `ApiError(0)`). */
  status: number;
  code: string | null;
  /** 포인트를 섞은 승인에서만. 서버가 토스 승인 **뒤** SPEND 가 안 섰을 때 싣는다: `true` 카드 몫을 되돌렸다 ·
   *  `false` 되돌렸는지 모른다. 없으면(`null` · 빠짐) 토스를 부르기 전의 거절이다(`lib/points-pay.ts`). */
  cardReverted?: boolean | null;
}

/** 같은 키로 잠깐 뒤 다시 보내면 될 수 있는 실패인가. **서버가 PG 승인을 부르지 않은 409 다.**
 *  `POINTS_BUSY` 는 포인트를 섞은 승인에서 원장 순번을 못 쥔 409 다. 토스를 부르기 전이라 같은 갈래다. */
export function isConfirmBusy(failure: ConfirmFailure | null): boolean {
  return (
    failure !== null &&
    failure.status === 409 &&
    (failure.code === "PAYMENT_IN_PROGRESS" || failure.code === "POINTS_BUSY")
  );
}

/** **이 키로 더 할 일이 없다고 서버가 코드로 말한 것들.** 여기 있는 코드만 키를 버린다.
 *
 * - `PAYMENT_DECLINED`: 거절됐다. 돈이 안 움직였다
 * - `PAYMENT_AMOUNT_MISMATCH` · `PAYMENT_ORDER_MISMATCH`: 같은 값으로 몇 번을 보내도 같은 답이다
 * - `PAYMENT_ALREADY_PAID`: 다른 결제가 이미 서 있다
 * - `NOTHING_TO_PAY` · `TOO_MANY_IMAGES`: 결제할 것이 맞지 않는다. PG 를 부르기 전에 끊긴다
 *
 * ⛔ **목록에 없는 것은 전부 들고 있다**(아래 `confirmKeyFate`). 거꾸로 「들고 있을 것」을
 * 나열하면 목록 밖의 실패가 조용히 키를 버린다. */
export const CONFIRM_KEY_FINISHED_CODES: readonly string[] = [
  "PAYMENT_DECLINED",
  "PAYMENT_AMOUNT_MISMATCH",
  "PAYMENT_ORDER_MISMATCH",
  "PAYMENT_ALREADY_PAID",
  "NOTHING_TO_PAY",
  "TOO_MANY_IMAGES",
];

/** 승인이 실패로 끝난 뒤 결제 키를 들고 있을지(`KEEP`) 버릴지(`DROP`).
 *
 * **버리는 것은 서버가 「이 키는 끝났다」고 코드로 말한 경우뿐이다**(`CONFIRM_KEY_FINISHED_CODES`).
 * 나머지는 그 키의 승인이 판가름 나지 않았거나 우리가 모르는 갈래라 들고 있는다. 같은 키로 다시
 * 보내면 서버가 이어서 맞추거나(`PAYMENT_GATEWAY_FAILED` 의 복구) 저장된 결과를 준다.
 *   - `PAYMENT_IN_PROGRESS`: 잠금을 못 쥐어 PG 를 안 불렀다
 *   - 응답을 못 받음(`status` 0): 서버에 닿았는지 모른다
 *   - `PAYMENT_GATEWAY_FAILED`: 돈이 움직였는지 모른다
 *   - ⛔⛔ **코드 없는 5xx**: PG 승인이 선 뒤 장부를 적다 D1 이 터지면(`lib/checkout.ts`) Hono 기본
 *     500 평문이 오고, Cloudflare 엣지는 502·503·504 HTML 을 준다. **돈은 나갔는데 작업은 결제
 *     전으로 남는다.** 같은 키 재전송이 유일한 복구 길이라 버리면 안 된다
 *   - 401(세션 만료): PG 를 안 불렀다. 버릴 까닭이 없다. 로그인 게이트가 돌아올 주소에 질의
 *     문자열까지 싣고 가므로(`SessionGate` 의 `from`) 그 주소로 돌아오면 같은 키로 이어진다.
 *     ⚠️ 로그인 서버의 돌아올 주소 허용 목록이 질의를 남기는지는 이 회차에서 확인하지 않았다
 *   - 판정할 수 없는 실패(`null`): 모르는 것은 버리지 않는다
 * ⛔ 이 갈래에서 키를 버리고 새 결제위젯을 열면 **같은 작업에 승인이 둘 걸릴 수 있다.** */
export function confirmKeyFate(failure: ConfirmFailure | null): "KEEP" | "DROP" {
  if (failure === null || failure.code === null) return "KEEP";
  if (CONFIRM_KEY_FINISHED_CODES.includes(failure.code)) return "DROP";
  // 포인트를 섞은 승인(`/api/payments/confirm-with-points`). 아래 「포인트 쪽 거절」 머리말.
  if (POINTS_REFUSED_BEFORE_CARD_CODES.includes(failure.code)) return "DROP";
  const reverted = failure.cardReverted ?? null;
  if (POINTS_REFUSED_CODES.includes(failure.code)) return reverted === false ? "KEEP" : "DROP";
  if (failure.code === "POINTS_SPEND_FAILED") return reverted === true ? "DROP" : "KEEP";
  return "KEEP";
}

/** **포인트 쪽 거절**(섞어 낸 승인). 서버 차례는 ① 스위치 → ② 금액 대조 → ③ 잔액 → ④ 토스 승인 → ⑤ SPEND 다.
 *
 * - `POINTS_SPEND_OFF` · `POINTS_SPLIT_MISMATCH`: ① · ② 에서만 난다. **토스를 부르기 전**이라 키는 끝났다
 * - `POINTS_INSUFFICIENT` · `POINTS_DEBT` · `POINTS_ACCOUNT_NOT_ACTIVE`: ③ 이면(`cardReverted` 없음) 토스 전이고,
 *   ⑤ 이면 카드를 되돌린 뒤다(`true`). 둘 다 키가 끝났다. ⛔ **`false`(되돌렸는지 모른다)만은 들고 있는다.** 같은 키로
 *   다시 보내면 서버가 그 결제를 이어서 맞춘다
 * - `POINTS_SPEND_FAILED`: ⑤ 에서 원장을 못 썼다. 카드를 되돌렸다고 서버가 말한 경우(`true`)만 버린다 */
export const POINTS_REFUSED_BEFORE_CARD_CODES: readonly string[] = ["POINTS_SPEND_OFF", "POINTS_SPLIT_MISMATCH"];
export const POINTS_REFUSED_CODES: readonly string[] = [
  "POINTS_INSUFFICIENT",
  "POINTS_DEBT",
  "POINTS_ACCOUNT_NOT_ACTIVE",
];

/** 섞어 낸 승인이 **토스를 부르기 전에** 포인트 쪽에서 거절됐을 때 화면이 할 일. 그 밖은 `null`(실패 패널).
 *  - `SPEND_OFF`: 스위치가 꺼졌다. 칸을 숨기고 금액을 다시 받는다
 *  - `RELOAD`: 금액 대조가 어긋났거나 그사이 잔액이 바뀌었다. 금액과 잔액을 다시 받아 칸을 새 숫자로 세운다
 *  - `CARD_REVERTED`: 카드 승인 뒤 되돌렸다(`cardReverted: true`). 금액을 다시 받고 되돌림 배너를 세운다
 * ⛔ 되돌렸는지 모르는 갈래(`cardReverted: false`)는 여기 오지 않는다. 키를 들고 실패 패널이 `다시 시도` 로 같은 키를 다시 보낸다. */
export function pointsConfirmFallback(
  failure: ConfirmFailure | null,
): "SPEND_OFF" | "RELOAD" | "CARD_REVERTED" | null {
  if (failure === null || failure.code === null) return null;
  if (failure.code === "POINTS_SPEND_OFF") return "SPEND_OFF";
  // 카드 승인 뒤 포인트를 못 빼 **카드를 되돌린 기록이 있다**. 돈은 돌아갔고 키는 끝났다. 금액을 다시 받아 되돌림 배너를 세운다.
  if (failure.code.startsWith("POINTS_") && failure.cardReverted === true) return "CARD_REVERTED";
  if (failure.code === "POINTS_SPLIT_MISMATCH") return "RELOAD";
  if (POINTS_REFUSED_CODES.includes(failure.code) && (failure.cardReverted ?? null) === null) return "RELOAD";
  return null;
}

/** 승인 요청이 끝난 뒤 화면이 할 일. **화면이 내려갔으면 아무것도 하지 않는다.** 내려간 뒤에
 *  주소를 비우면(`setSearch`) 셀러가 다른 화면에 가 있어도 이 주소로 되끌려 온다.
 *
 * @param outcome 승인됨(`null`) 또는 실패의 판정 모양
 * @returns `render` 화면을 바꿀지 · `clearAddress` 주소(결제 키)를 비울지 · `keepKey` 키를 들고 있을지 */
export function confirmSettlement(
  alive: boolean,
  outcome: { ok: true } | { ok: false; failure: ConfirmFailure | null },
): { render: boolean; clearAddress: boolean; keepKey: boolean } {
  if (!alive) return { render: false, clearAddress: false, keepKey: true };
  if (outcome.ok) return { render: true, clearAddress: true, keepKey: false };
  const keepKey = confirmKeyFate(outcome.failure) === "KEEP";
  return { render: true, clearAddress: !keepKey, keepKey };
}

/** 지금 화면이 결제 키를 들고 있는가. 확정을 보내는 중이거나, 키를 들고 실패한 경우다. */
export function holdsPaymentKey(phase: { kind: string; keyHeld?: boolean }): boolean {
  return phase.kind === "CONFIRMING" || (phase.kind === "FAILED" && phase.keyHeld === true);
}

/** 셀러가 누른 버튼이 무엇을 할지.
 *
 * - `RETRY`(`다시 시도`): 키가 있으면 같은 키로 확정(`RECONFIRM`), 없으면 새 결제위젯
 * - `OTHER_CARD`(`카드 바꾸기`): 키가 있으면 **아무것도 안 한다**, 없으면 새 결제위젯
 * - `VIEW_PROGRESS`(`진행 보기`): 키가 있으면 **아무것도 안 한다**, 없으면 단계 화면으로.
 *   ⚠️ `/jobs/:id` 는 아직 결제 단계인 작업을 `/pay` 로 다시 보내는데 그때 질의 문자열이 빠져
 *   **키 없이 새 결제위젯이 선다.** 그래서 키를 들고 있는 동안은 막는다.
 *
 * ⛔ 판가름 안 난 키를 두고 새 결제를 열면 같은 작업에 승인이 둘 걸릴 수 있다. */
export function paymentActionOf(
  action: "RETRY" | "OTHER_CARD" | "VIEW_PROGRESS",
  keyHeld: boolean,
): "RECONFIRM" | "NEW_WIDGET" | "NAVIGATE" | "NONE" {
  if (action === "RETRY") return keyHeld ? "RECONFIRM" : "NEW_WIDGET";
  if (keyHeld) return "NONE";
  return action === "OTHER_CARD" ? "NEW_WIDGET" : "NAVIGATE";
}

/** 승인을 보내고, 「확인 중」이면 **같은 요청을** 정해 둔 간격으로 다시 보낸다.
 *
 * - `send` 는 매번 같은 요청을 보낸다(키 · 주문번호 · 금액을 바꾸지 않는다).
 * - `wait(ms)` 가 `false` 를 주면(화면이 내려갔다) 더 보내지 않고 마지막 실패를 던진다.
 * - 「확인 중」이 아닌 실패는 다시 보내지 않고 바로 던진다. 상한을 넘겨도 던진다. */
export async function confirmWithBusyRetry<T>(
  send: () => Promise<T>,
  describe: (err: unknown) => ConfirmFailure | null,
  wait: (ms: number) => Promise<boolean>,
  onBusy: (err: unknown) => void,
): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await send();
    } catch (err) {
      const delay = CONFIRM_BUSY_RETRY_DELAYS_MS[attempt];
      if (!isConfirmBusy(describe(err)) || delay === undefined) throw err;
      onBusy(err);
      if (!(await wait(delay))) throw err;
    }
  }
}

/** 다시 읽기가 끝났을 때 셀러를 데려갈지. **켜져 있을 때 한 번만** 데려가고 끈다. 화면을 떠나면
 *  꺼지므로(`LivePay` 의 `followProgress`) 늦게 끝난 다시 읽기는 아무 데도 데려가지 않는다. */
export function settleFollow(follow: { current: boolean }, go: () => void): void {
  if (!follow.current) return;
  follow.current = false;
  go();
}

/* ── 판정 끝 (pay-confirm-rules) ─────────────────────────────────────────── */

/** 포인트 쪽 거절 뒤 다시 세운 화면의 처음 고름. 셀러가 앞서 고른 갈래가 **새 잔액으로도 서면** 그대로 두고, 안 서면 비운다
 *  (PRD `7.7.3a`: 고른 갈래가 더는 서지 않으면 고름을 비우고 버튼을 끈다. 저절로 다른 갈래를 고르지 않는다). 그 밖에는 처음처럼 비어 있다. */
function carriedChoice(
  total: number,
  points: PointBalanceView | null,
  notice: PayNotice | null,
): WebPointChoice | null {
  if (notice === null || notice.kind === "POINTS_OFF") return null;
  return planForPay(total, points, notice.choice) !== null && pointsOffered(points) ? notice.choice : null;
}

/** `ApiError` 를 판정 구역이 읽는 모양으로. 그 밖의 예외는 판정할 수 없다(`null`). */
function describeConfirmFailure(err: unknown): ConfirmFailure | null {
  if (!(err instanceof ApiError)) return null;
  if (err instanceof PointsConfirmError) {
    return { status: err.status, code: err.code ?? null, cardReverted: err.cardReverted };
  }
  return { status: err.status, code: err.code ?? null };
}

/** 결제됨 보조줄의 결제한 곳(목업 `W-26` 결제됨: `포인트 ₩3,000 · 신한 1234 ₩1,000`).
 *  포인트를 안 썼으면 지금까지처럼 결제 수단 요약 하나다. 모르는 금액은 적지 않는다. */
function paidMethodParts(amount: number | null, methodLabel: string | null, pointAmount: number | null): (string | null)[] {
  if (pointAmount === null || pointAmount <= 0) return [methodLabel];
  const tossAmount = amount === null ? null : amount - pointAmount;
  return [
    `포인트 ${formatKrw(pointAmount)}`,
    methodLabel && tossAmount !== null && tossAmount > 0 ? `${methodLabel} ${formatKrw(tossAmount)}` : null,
  ];
}

/** `W-26` 오른쪽 패널의 `상품 종류`. **고르는 칸이 아니라 진술의 기록이 남는 칸이다.**
 *  (PRD `7.6.9b` · 법무 `docs/LEGAL_IP_GUIDE.md` `6.2.1`(2))
 *
 * ⛔ **기본값을 골라 두지 않는다.** 아무것도 안 골라진 채로 선다 — 미리 켜 두면 **셀러가
 * 하지 않은 진술이 셀러 이름으로 남는다.**
 *
 * ⛔ **이 섹션이 결제를 막지 않는다.** 왼쪽의 `결제하고 시작` 과 아무 관계가 없고, 진술
 * 때문에 서는 것은 **상품명 만들기 하나**다(`W-30` 의 `A-50b` 상태).
 *
 * ⛔ **한글을 이 함수에 적지 않는다.** 앱 시트(`A-26c`)와 **같은 상수**를 읽는다. 두 화면이
 * 각자 적으면 같은 진술을 두 기기가 다른 문장으로 받는다(PRD `7.7.3`).
 */
function ProductCategorySection({
  jobId,
  view,
}: {
  jobId: string | undefined;
  view: DetailJobView | null;
}) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  // ⛔ **`undefined`(옛 서버)와 `null`(안 고르셨다)을 같이 본다.** 화면이 할 일이 같다.
  const stated = view?.productCategoryStatement ?? null;
  // 작업이 아직 안 왔으면 적을 곳이 없다. 셸이 작업을 받은 뒤에만 이 화면이 서므로 거의
  // 오지 않는 자리다.
  const locked = !jobId || !view;

  function choose(statement: ProductCategoryStatement) {
    if (locked || saving || !jobId) return;
    setSaving(true);
    setFailed(false);
    void stateProductCategory(jobId, statement)
      .then(({ productCategoryStatement }) => {
        // 응답이 기록 전체를 들고 오므로 다시 조회하지 않는다. ⛔ **응답을 버리고 화면
        // 상태만 바꾸지 마라** — 그러면 서버에 안 남은 진술을 화면이 보여준다.
        queryClient.setQueryData(["job", jobId], (previous: DetailJobView | undefined) =>
          previous ? { ...previous, productCategoryStatement } : previous,
        );
      })
      .catch(() => setFailed(true))
      .finally(() => setSaving(false));
  }

  return (
    <section>
      <h3>{CATEGORY_COPY.rowLabel}</h3>
      <div className="setgrp">
        {PRODUCT_CATEGORY_STATEMENT_OPTIONS.map((option) => {
          const selected = stated?.statement === option.value;
          return (
            <button
              type="button"
              key={option.value}
              // 스와치 칸을 뺀 2열. 목업 `A-26c`·`W-26` 이 같은 인라인 값을 쓴다.
              style={{ gridTemplateColumns: "16px minmax(0, 1fr)" }}
              className={`optrow${selected ? " on" : ""}`}
              role="radio"
              aria-checked={selected}
              disabled={locked || saving}
              onClick={() => choose(option.value)}
            >
              <span className="rd" aria-hidden="true" />
              {/* ⭐ **고를 값 자체가 진술문이다.** 분류 명사로 줄이면 약속이 남지 않는다. */}
              <span>{option.sentence}</span>
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 9 }}>
        {/* ⭐ **첫 줄이 코드가 지켜야 하는 약속이다.** 둘째 줄이 막히는 범위를 먼저 좁힌다 —
            이 말이 없으면 셀러는 상품을 못 올리는 줄 안다. */}
        <LimitBanner>
          <span className="l1">{CATEGORY_COPY.recordNotice}</span>
          <span className="l2">{CATEGORY_COPY.sheetBody}</span>
        </LimitBanner>
      </div>
      {stated ? (
        // **고르신 것과 시각을 되비친다.** 배너가 한 약속을 셀러가 확인할 수 있어야 한다.
        <p className="hint">{`${stated.sentence} · ${stated.statedAt}`}</p>
      ) : (
        // ⛔ **재촉하지 않는다.** `필수`·`아직 안 고르셨습니다` 를 쓰지 않고, 안 고른 대가
        //    하나만 말한다.
        <p className="hint">{CATEGORY_COPY.skipNotice}</p>
      )}
      {failed ? (
        <p className="hint" role="status">
          기록하지 못했습니다. 잠시 뒤 다시 고르시면 됩니다. 그동안에도 사진은 그대로 만들어
          드립니다.
        </p>
      ) : null}
    </section>
  );
}
